import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import LoadingWheel from "../../components/shared/LoadingWheel";
import ShippingPoliciesTable from "../../components/shippingPolicies/ShippingPoliciesTable";
import useShippingPolicies from "../../data/useShippingPolicies";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { PlusIcon } from "@heroicons/react/24/outline";
import ShippingPolicyModal from "../../components/shippingPolicies/ShippingPoliciesModal";
import { IShippingPolicy } from "./../../models/ShippingPolicy";
import usePartPro from "../../data/usePartPro";
import useNotification from "../../components/notifications/useNotifications";

export default function ShippingPolicies() {
  const { setInfo } = usePageNameContext();
  const { shippingPolicies } = useShippingPolicies();
  const { connection: partProConnection, syncShippingPolicies } = usePartPro();
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<IShippingPolicy | undefined>(undefined);

  const partProConnected = !!partProConnection.data?.data?.connected;

  useEffect(() => {
    setInfo({
      name: "Shipping Policies",
      desc: "View and configure your shipping policies",
    });
  }, []);

  const handleEditPolicy = (policy: IShippingPolicy) => {
    setSelectedPolicy(policy);
    setOpen(true);
  };

  const handleAddNewPolicy = () => {
    setSelectedPolicy(undefined);
    setOpen(true);
  };

  const handleSyncPartPro = () => {
    syncShippingPolicies.mutate(undefined, {
      onSuccess: (res) => {
        const count = res?.data?.length ?? 0;
        addNotification({
          variant: "success",
          primaryText: `Synced ${count} shipping polic${count === 1 ? "y" : "ies"} to PartPro`,
        });
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to sync shipping policies to PartPro",
        });
      },
    });
  };

  if (shippingPolicies.isLoading) {
    return (
      <div>
        <LoadingWheel />
      </div>
    );
  }

  return (
    <>
      <ShippingPolicyModal open={open} setOpen={setOpen} policy={selectedPolicy} />
      <div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <ButtonInput isSubmit={false} onClick={() => handleAddNewPolicy()} classes={undefined}>
            <div className="bg-primary-600 px-3 py-2 shadow rounded text-white flex items-center">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Shipping Policy
            </div>
          </ButtonInput>

          {partProConnected && (
            <ButtonInput
              label={syncShippingPolicies.isLoading ? "Syncing to PartPro…" : "Sync to PartPro"}
              isSubmit={false}
              onClick={handleSyncPartPro}
              classes={undefined}
              isLoading={syncShippingPolicies.isLoading}
              disabled={syncShippingPolicies.isLoading}
            />
          )}
        </div>

        <ShippingPoliciesTable
          policies={shippingPolicies.data?.data!}
          onEditClick={handleEditPolicy}
        />
      </div>
    </>
  );
}
